import React, { useEffect, useState } from 'react';
import { sceneLineLabel } from '../../utils/plottrackGrid';
import {
  ACCEPT_CHAPTER_BREAK,
  CHAPTER_BREAK_COULD_NOT_PLACE,
  CHAPTER_BREAK_COULD_NOT_SUGGEST,
  CHAPTER_SEED_LEDE,
  CHAPTER_SEED_NONE,
  DISMISS_CHAPTER_BREAK,
  END_CHAPTER_AFTER,
  PLACE_CHAPTER_BREAK,
  SUGGEST_CHAPTER_BREAKS,
  SUGGESTED_CHAPTER_BREAK,
} from '../../utils/writerCopy';

function seedErrorCopy(err, fallback) {
  const raw = String(err?.message || '').trim();
  const stripped = raw.replace(/^VALIDATION:\s*[^-]+-\s*/, '').trim();
  return stripped || fallback;
}

function suggestionCopy(suggestion, columns) {
  const index = (columns || []).findIndex(
    (column) => String(column.scene_id) === String(suggestion.after_scene_id),
  );
  const scene = index >= 0 ? columns[index] : null;
  const where = scene
    ? ` after ${sceneLineLabel(scene, index)}`
    : (suggestion.after_scene_number ? ` after scene ${suggestion.after_scene_number}` : '');
  const reason = String(suggestion.reason || '').trim();
  return reason
    ? `${SUGGESTED_CHAPTER_BREAK}${where} · ${reason}`
    : `${SUGGESTED_CHAPTER_BREAK}${where}`;
}

export default function PlotTrackChapterSeed({
  columns,
  suggestions,
  onSuggest,
  onAccept,
  onDismiss,
  onPlace,
}) {
  const line = columns || [];
  const [afterSceneId, setAfterSceneId] = useState(String(line[0]?.scene_id || ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [emptySuggest, setEmptySuggest] = useState(false);

  useEffect(() => {
    const next = columns || [];
    if (!next.some((column) => String(column.scene_id) === String(afterSceneId))) {
      setAfterSceneId(String(next[0]?.scene_id || ''));
    }
  }, [columns, afterSceneId]);

  if (line.length === 0) return null;

  const run = async (fn, fallback) => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(seedErrorCopy(err, fallback));
      return undefined;
    } finally {
      setBusy(false);
    }
  };

  const suggest = async () => {
    setEmptySuggest(false);
    const next = await run(() => onSuggest(), CHAPTER_BREAK_COULD_NOT_SUGGEST);
    if (Array.isArray(next) && next.length === 0) setEmptySuggest(true);
  };

  const place = async () => {
    if (!afterSceneId || !onPlace) return;
    setEmptySuggest(false);
    await run(() => onPlace(afterSceneId), CHAPTER_BREAK_COULD_NOT_PLACE);
  };

  return (
    <section className="pt-chapter-seed" aria-label="Chapters">
      <p className="pt-chapter-seed__lede">{CHAPTER_SEED_LEDE}</p>
      <div className="pt-chapter-seed__actions">
        <button
          type="button"
          onClick={suggest}
          disabled={busy || !onSuggest}
        >
          {SUGGEST_CHAPTER_BREAKS}
        </button>
        <label className="pt-chapter-seed__place">
          <span>{END_CHAPTER_AFTER}</span>
          <select
            value={afterSceneId}
            aria-label={END_CHAPTER_AFTER}
            onChange={(event) => setAfterSceneId(event.target.value)}
          >
            {line.map((column, index) => (
              <option key={column.scene_id} value={column.scene_id}>
                {sceneLineLabel(column, index)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={place}
          disabled={busy || !afterSceneId || !onPlace}
        >
          {PLACE_CHAPTER_BREAK}
        </button>
      </div>
      {emptySuggest && <p className="pt-chapter-seed__empty">{CHAPTER_SEED_NONE}</p>}
      {error && <p className="pt-chapter-seed__error">{error}</p>}
      {(suggestions || []).length > 0 && (
        <ul className="pt-chapter-seed__suggestions">
          {(suggestions || []).map((suggestion) => (
            <li key={String(suggestion.after_scene_id)} className="pt-chapter-seed__suggestion">
              <span>{suggestionCopy(suggestion, line)}</span>
              <button
                type="button"
                onClick={() => run(() => onAccept(suggestion.after_scene_id), CHAPTER_BREAK_COULD_NOT_PLACE)}
                disabled={busy || !onAccept}
              >
                {ACCEPT_CHAPTER_BREAK}
              </button>
              <button
                type="button"
                onClick={() => onDismiss(suggestion.after_scene_id)}
                disabled={busy || !onDismiss}
              >
                {DISMISS_CHAPTER_BREAK}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
